import { useEffect, useState, useRef } from 'react';
import InlineHexagonLoader from '../HexagonLoader/InlineHexagonLoader';
import { useAuth } from '../../context/AuthContext';
import { useAIService } from '../../context/AIServiceContext';
import InsightMarkdown from './InsightMarkdown';
import AiNarrativeFallback from './AiNarrativeFallback';
import * as Sentry from '@sentry/react';

// Increased retry delay to 120 seconds for better rate limit handling
const RETRY_DELAY_MS = 120000; // 120s (increased from 60s)
const MAX_RETRIES = 3;
// No stagger delay for executive summary (loads first)

const ExecutiveSummary = ({ scope = 'it-executive-summary' }) => {
  const { apiFetch } = useAuth();
  const { aiEnabled, isLoading: aiLoading } = useAIService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [reloadToken, setReloadToken] = useState(0); // increments to trigger refetch
  const retryTimeoutRef = useRef(null);
  const attemptRef = useRef(0);

  // Cleanup any pending retry timer on unmount
  useEffect(() => {
    return () => { if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current); };
  }, []);

  // Fetch summary only when AI is enabled (from context)
  useEffect(() => {
    // Wait for AI config to load
    if (aiLoading) return;
    
    // If AI is disabled, don't fetch
    if (!aiEnabled) return;

    let active = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await apiFetch(`/api/insights/${scope}`);
        if (res.status === 204) { // No Content -> nothing to render
          if (active) { setText(''); attemptRef.current = 0; }
          return;
        }
        if (res.status === 429) throw new Error('RATE_LIMIT');
        if (res.status === 403) throw new Error('FORBIDDEN');
        if (!res.ok) throw new Error('Failed to fetch insights');
        const json = await res.json().catch(() => null);
        if (active && json) { setText(json.summary || ''); attemptRef.current = 0; }
      } catch (e) {
          Sentry.captureException(e);
        if (!active) return;
        const msg = e.message || 'Error';
        // Rate limit handling with scheduled retry
        if (msg === 'RATE_LIMIT' && attemptRef.current < MAX_RETRIES) {
            const attempt = attemptRef.current + 1;
            attemptRef.current = attempt;
            setError(`Service busy (429). Retrying in 2 minutes (attempt ${attempt}/${MAX_RETRIES})`);
            retryTimeoutRef.current = setTimeout(() => {
              setReloadToken(t => t + 1); // triggers refetch
            }, RETRY_DELAY_MS);
        } else if (msg === 'FORBIDDEN') {
            setError('AI Analytics is disabled or API key is invalid. Please check settings.');
        } else {
            setError(msg === 'RATE_LIMIT' ? 'Service busy. Please try again later.' : msg);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [aiEnabled, aiLoading, scope, apiFetch, reloadToken]);

  // If AI is loading config, show placeholder
  if (aiLoading) {
    return (
      <div className="dashboard-card insight-card" style={{ marginBottom: 12 }}>
        <h2 className="insight-title" style={{ marginTop: 0, fontSize: 18 }}>Executive Summary</h2>
        <div style={{ padding: '24px 0', textAlign: 'center', color: '#666' }}>
          <InlineHexagonLoader />
          <div style={{ marginTop: 12, fontSize: 14 }}>Waiting for AI Insights...</div>
        </div>
      </div>
    );
  }
  
  // If AI is disabled, show a clear deterministic note instead of disappearing —
  // the report is a handover-grade source of truth and must not leave silent gaps.
  if (!aiEnabled) return <AiNarrativeFallback variant="executive" />;

  // Show placeholder while loading
  if (loading && !text && !error) {
    return (
      <div className="dashboard-card insight-card" style={{ marginBottom: 12 }}>
        <h2 className="insight-title" style={{ marginTop: 0, fontSize: 18 }}>Executive Summary</h2>
        <div style={{ padding: '24px 0', textAlign: 'center', color: '#666' }}>
          <InlineHexagonLoader />
          <div style={{ marginTop: 12, fontSize: 14 }}>Generating AI insights...</div>
        </div>
      </div>
    );
  }
  
  if (!text && error) return <div className="dashboard-card" style={{ color: error.startsWith('Service busy') ? '#b36b00' : 'red' }}>Insights: {error}</div>;
  if (!text) return null;

  return (
    <div className="dashboard-card insight-card" style={{ marginBottom: 12 }}>
      <h2 className="insight-title" style={{ marginTop: 0, fontSize: 18 }}>Executive Summary</h2>
      <InsightMarkdown text={text} />
      {error && error.startsWith('Service busy') && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#b36b00' }}>{error}</div>
      )}
    </div>
  );
};

export default ExecutiveSummary;
